import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { Input, Text } from "@src/appV2/redesign/system/ui-components";
import { type Control, Controller } from "react-hook-form";

import { type ReferenceFormData } from "../types";
import { getReferenceFormFieldErrorId, getReferenceFormFieldId } from "../utils/formHelpers";

interface InputFormFieldProps {
  formId: string;
  name: keyof ReferenceFormData;
  control: Control<ReferenceFormData>;
  label: string;
  placeholder?: string;
  type?: "text" | "tel" | "email";
  inputMode?: "text" | "tel" | "email";
  enterKeyHint?: string;
  onEnterPress?: () => void;
}

export function InputFormField({
  formId,
  name,
  control,
  label,
  placeholder,
  type,
  inputMode = "text",
  enterKeyHint = "next",
  onEnterPress,
}: InputFormFieldProps) {
  return (
    <Controller
      name={name}
      control={control}
      render={({
        field: { ref, ...field },
        fieldState: { error },
        formState: { isSubmitting },
      }) => {
        const fieldId = getReferenceFormFieldId(formId, field.name);
        const errorId = getReferenceFormFieldErrorId(fieldId);

        return (
          <Stack spacing={2}>
            <label htmlFor={fieldId}>
              <Text inline variant="body1">
                {label}
              </Text>
            </label>
            <Input
              {...field}
              fullWidth
              inputRef={ref}
              size="large"
              type={type}
              error={isDefined(error)}
              disabled={isSubmitting}
              id={fieldId}
              placeholder={placeholder}
              aria-describedby={isDefined(error) ? errorId : undefined}
              inputProps={{
                enterKeyHint,
                inputMode,
              }}
              onKeyDown={(event) => {
                if (isDefined(onEnterPress) && event.key === "Enter") {
                  event.preventDefault();
                  onEnterPress();
                }
              }}
            />
            {isDefined(error) && (
              <Text
                id={errorId}
                variant="caption"
                color="error"
                sx={{ paddingLeft: 1 }}
                role="alert"
              >
                {error.message}
              </Text>
            )}
          </Stack>
        );
      }}
    />
  );
}
